import React, { useEffect, useState } from 'react';
import { DashboardStats, Project, WorkRecord } from '@/types';
import apiService from '@/services/api';
import Card from '@/components/ui/Card';
import { Activity } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useUserRole } from '@/hooks/useUserRole';
import { useViewportHeight } from '@/hooks/useViewportHeight';
import UserSections from '@/components/Dashboard/UserSections';
import AdminSections from '@/components/Dashboard/AdminSections';
import ManagerSections from '@/components/Dashboard/ManagerSections';

const Dashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [managedProjects, setManagedProjects] = useState<Project[]>([]);
  const [recentWorkRecord, setRecentWorkRecord] = useState<WorkRecord | null>(null);
  const [error, setError] = useState('');
  const { user } = useAuth();
  
  // 使用新的hooks
  const { upperSectionHeight, lowerSectionHeight, singleLayerHeight } = useViewportHeight();
  const { shouldShowSingleLayer, shouldShowManagerSection, shouldShowAdminSection } = useUserRole(managedProjects);

  useEffect(() => {
    fetchDashboardData();
  }, [user]);

  const fetchDashboardData = async () => {
    try {
      // 并行获取所有需要的数据
      const [dashboardStats, userProjects, workRecords] = await Promise.all([
        apiService.getDashboardStats(),
        apiService.getProjects(),
        apiService.getWorkRecords({ limit: 1, skip: 0 })
      ]);
      
      setStats(dashboardStats);
      setProjects(userProjects);
      setRecentWorkRecord(workRecords.length > 0 ? workRecords[0] : null);
      
      // 如果用户是管理员或经理，获取管理的项目
      if (user && (user.role === 'admin' || user.role === 'manager')) {
        const allProjects = await apiService.getProjects();
        const managed = allProjects.filter(p => p.manager_id === user.id);
        setManagedProjects(managed);
      }
    } catch (error: any) {
      setError('获取控制台数据失败');
    } finally {
      setLoading(false);
    }
  };


  if (loading) {
    return (
      <div className="h-screen flex flex-col p-6">
        {user?.role !== 'admin' && (
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-3xl font-bold text-gray-900">控制台</h1>
            <div className="flex items-center space-x-2 text-sm text-gray-500">
              <Activity className="h-4 w-4" />
              <span>加载中...</span>
            </div>
          </div>
        )}
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">正在加载数据...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-screen flex flex-col p-6">
        {user?.role !== 'admin' && (
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-3xl font-bold text-gray-900">控制台</h1>
            <div className="flex items-center space-x-2 text-sm text-gray-500">
              <Activity className="h-4 w-4" />
              <span>出现错误</span>
            </div>
          </div>
        )}
        <div className="flex-1 flex items-center justify-center">
          <Card className="max-w-md w-full">
            <Card.Content className="p-6 text-center">
              <p className="text-red-600 mb-4">{error}</p>
              <button
                onClick={fetchDashboardData}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                重试
              </button>
            </Card.Content>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col p-6 overflow-hidden">
      {/* Header - 仅非管理员显示 */}
      {user?.role !== 'admin' && (
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-gray-900">控制台</h1>
          <div className="flex items-center space-x-2 text-sm text-gray-500">
            <Activity className="h-4 w-4" />
            <span>实时数据</span>
          </div>
        </div>
      )}

      {/* 根据用户角色显示不同布局 */}
      {user?.role === 'admin' ? (
        /* 管理员：只显示管理统计信息，全屏显示 */
        <div className="flex-1 overflow-hidden">
          <AdminSections maxHeight={singleLayerHeight} />
        </div>
      ) : shouldShowSingleLayer ? (
        /* 普通员工：单层布局 */
        <div className="flex-1">
          <UserSections 
            projects={projects}
            recentWorkRecord={recentWorkRecord}
            maxHeight={singleLayerHeight}
          />
        </div>
      ) : (
        /* 非管理员的管理者：上下两层布局 */
        <div className="flex-1 flex flex-col space-y-6 overflow-hidden">
          {/* 上层：用户基础信息 */}
          <div className="flex-shrink-0">
            <UserSections 
              projects={projects}
              recentWorkRecord={recentWorkRecord}
              maxHeight={upperSectionHeight}
            />
          </div>

          {/* 下层：管理统计信息 */}
          <div className="flex-1 overflow-hidden">
            {shouldShowManagerSection ? (
              <ManagerSections 
                managedProjects={managedProjects}
                maxHeight={lowerSectionHeight}
              />
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;